import {
    Component,
    computed,
    effect,
    inject,
    Injectable,
    signal
} from "@angular/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";

import {
    distinctUntilChanged,
    lastValueFrom
} from "rxjs";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogActionButtonsComponent,
    TilbyDialogContentComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    BaseInputButton
} from "@tilby/tilby-ui-lib/models";

import {
    TranslateModule,
} from "@ngx-translate/core";

import {
    TilbyCurrencyPipe
} from "@tilby/tilby-ui-lib/pipes/tilby-currency";

import {
    KeyValue,
    KeyValuePipe
} from "@angular/common";

import {
    ReactiveFormsModule,
    Validators
} from "@angular/forms";

import {
    CustomForm,
    CustomFormControl,
    CustomFormControlProps,
    CustomFormGroup,
} from "@tilby/tilby-ui-lib/components/tilby-magic-form";

import {
    CashregisterKeypadComponent
} from "src/app/features/cashregister/components/cashregister-keyboard/cashregister-keypad";

import {
    TilbyKeypadDisplayComponent
} from "src/app/features/cashregister/components/cashregister-keyboard/tilby-keypad-display";

import {
    subscribeInComponent
} from "src/app/shared/angular-utils";

import {
    MatIcon
} from "@angular/material/icon";

import {
    MatIconButton
} from "@angular/material/button";

import {
    CashregisterStateService
} from "src/app/features";

import {
    TIPS_DEFAULT_VALUES
} from "src/app/core/constants/cashregister-settings";

export { TIPS_DEFAULT_VALUES };
export type TipValue = number;
export type Tip = KeyValue<string, TipValue> & { isCustom?: boolean };

type TipTotalFormValue = {
    cashValue: string;
}

export type TipDialogData = {
    finalAmount: number,
    tipsValues?: number[]
};

/**
 * Gestisce l'utilizzo della tip dialog di Tilby.
 */

@Component({
    selector: "app-tip-dialog",
    standalone: true,
    imports: [
        TilbyDialogContentComponent,
        TilbyDialogActionButtonsComponent,
        MatDialogModule,
        TranslateModule,
        TilbyCurrencyPipe,
        KeyValuePipe,
        ReactiveFormsModule,
        CashregisterKeypadComponent,
        TilbyKeypadDisplayComponent,
        MatIcon,
        MatIconButton,
    ],
    templateUrl: "./tip-dialog.component.html",
    styleUrls: ["./tip-dialog.component.scss"],
    host: {
        class: 'tw-overflow-hidden'
    }
})
export class TipDialogComponent {
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    protected readonly data: TipDialogData = inject(MAT_DIALOG_DATA);
    private readonly cashregisterStateService = inject(CashregisterStateService);

    protected keyboardFontSize = computed(() => this.cashregisterStateService.keyboardFontSizeSignal());

    protected totalValueForm = new CustomFormGroup<CustomForm<TipTotalFormValue>>({
        cashValue: new CustomFormControl('0',
            {
                validators: Validators.pattern(/^\d+(,\d{2})?$/)
            }, {
            ...new CustomFormControlProps(),
            label: 'Total',
            matElementClass: "tw-align-sub tw-text-right",
            readonly: true
        })
    });

    protected tipsChoices = new Map<string, Tip>([
        { key: 'DIALOG.TIP.NO_TIP', value: 0 },
        ...(this.data.tipsValues || TIPS_DEFAULT_VALUES).map(tip => ({
            key: `${tip}%`,
            value: this.data.finalAmount * tip / 100
        })),
        { key: 'DIALOG.TIP.CUSTOM_TIP', isCustom: true, value: 0 }
    ].map(tip => [tip.key, { ...tip, selected: false }])
    );
    protected tipSelected = signal([...this.tipsChoices.values()][Math.floor(this.tipsChoices.size / 2)], { equal: (a, b) => a.key == b.key && a.value == b.value });
    private tipSelectedBeforeCustomSelection = this.tipSelected();
    protected showKeypad = signal(false);
    protected totalAmount = computed(() => this.data.finalAmount + (this.tipSelected()?.value || 0));
    protected totalPlusTip = computed(() => `${this.tilbyCurrencyPipe.transform(this.data.finalAmount)} + ${this.tilbyCurrencyPipe.transform(this.tipSelected().value || 0)}`);
    protected buttons: BaseInputButton[] = [{
        name: "DIALOG.TIP.CONTINUE",
        class: "tilby-body-background-primary-color tw-px-10 tw-py-6 tw-text-2xl",
        click: () => this.closeDialogOnConfirm(),
    }];

    protected keyValueNoSort() {
        return 0;
    }

    constructor() {
        subscribeInComponent(
            this.cashValueController.valueChanges.pipe(distinctUntilChanged()),
            value => this.tipSelected.update(tip => ({ ...tip, value: this.getKeypadValue() }))
        );
        effect(() => this.onFieldChange(`${this.tipSelected().value}`.replace('.', ',')));
    }

    get cashValueController() {
        return this.totalValueForm.controls.cashValue;
    }

    get cashValue() {
        return this.cashValueController.value;
    }

    protected onFieldChange(totalChanged: string) {
        this.cashValueController.setValue(totalChanged, { emitEvent: true });
    }

    private getKeypadValue() {
        return parseFloat(this.cashValue.replace(',', '.'));
    }

    protected selectTip(tip: Tip) {
        if (tip.isCustom) {
            this.tipSelectedBeforeCustomSelection = this.tipSelected();
            this.showKeypad.set(true);
        }

        this.tipSelected.set(tip);
    }

    protected closeDialogOnCancel() {
        if (this.showKeypad()) {
            this.tipSelected.set(this.tipSelectedBeforeCustomSelection);
            this.showKeypad.set(false);
            return;
        }

        this.matDialogRef.close();
    }

    protected async closeDialogOnConfirm() {
        if (this.showKeypad()) {
            this.showKeypad.set(false);
            return;
        }

        this.matDialogRef.close(this.tipSelected().value);
    }
}

@Injectable({
    providedIn: "root",
})
export class TipDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(config: NonNullableConfigData<TipDialogData>) {
        const data: TipDialogData = config.data;
        const configEdited: NonNullableConfigData<TipDialogData> = {
            ...config,
            ...this.switchMobileDesktopDimensions({ width: "90vw", maxWidth: "1200px" }),
            disableClose: true,
            panelClass: "sm:tw-max-w-fit",
            data
        };
        return lastValueFrom(
            this.dialogRef.open<TipDialogComponent, TipDialogData, number>(TipDialogComponent, configEdited).afterClosed()
        );
    }
}
